import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from "@angular/router";
import { BehaviorSubject, Observable, Subject } from "rxjs";

import { FuseUtils } from "@fuse/utils";
import { UtilitesService } from "app/base-module/base-service/utilites.service";
import { AuthService } from "app/base-module/base-service/auth.service";

@Injectable()
export class ChatService implements Resolve<any>
{
    contacts!: any[];
    chats!: any[];
    user: any;
    onChatSelected: BehaviorSubject<any>;
    onChatSelectedNow:any;
    onContactSelected: BehaviorSubject<any>;
    onChatsUpdated: Subject<any>;
    onUserUpdated: Subject<any>;
    onLeftSidenavViewChanged: Subject<any>;
    onRightSidenavViewChanged: Subject<any>;

    /**
     * Constructor
     *
     * @param {HttpClient} _httpClient
     */
    constructor(
        private _httpClient: HttpClient,
        public utilitesService: UtilitesService,
        private authService: AuthService,
    )
    {
        // Set the defaults
        this.onChatSelected = new BehaviorSubject(null);
        this.onContactSelected = new BehaviorSubject(null);
        this.onChatsUpdated = new Subject();
        this.onUserUpdated = new Subject();
        this.onLeftSidenavViewChanged = new Subject();
        this.onRightSidenavViewChanged = new Subject();

        this.user = JSON.parse(this.authService.getUser() ?? "{}"); 
    }

    /**
     * Resolver
     *
     * @param {ActivatedRouteSnapshot} route
     * @param {RouterStateSnapshot} state
     * @returns {Observable<any> | Promise<any> | any}
     */
    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> | Promise<any> | any
    {
        return new Promise((resolve, reject) => {
            Promise.all([
                this.getContacts(),
                this.getChats(),
                this.getUser()
            ]).then(
                ([contacts, chats, user]) => {
                    this.contacts = contacts;
                    this.chats = chats;
                    this.user = user;
                },
                reject
            );
        });
    }

    /**
     * Get chat
     *
     * @param contactId
     * @returns {Promise<any>}
     */
    getChat(contactId:any): Promise<any>
    {        
        const chatItem = this.user.chatList.find((item:any) => {            
            return item.contactId == contactId;
            
        });
        
        // Create new chat, if it's not created yet.
        if ( !chatItem )
        {
            return this.createNewChat(contactId).then((newChats) => {
                this.getChat(contactId);
            });
        }

        return new Promise((resolve, reject) => {
            this._httpClient.get( this.utilitesService.urlPrefix + "chat/mensagens/"+ chatItem.id, { headers: this.authService.getAuthHeaders() })
                .subscribe((response: any) => {
                    const chat = response.data;

                    const chatContact = this.contacts.find((contact) => {
                        return contact.id == contactId;
                    });

                    const chatData = {
                        chatId : chat.id,
                        dialog : chat.dialog,
                        contact: chatContact
                    };

                    this.onChatSelected.next({...chatData});
                    this.onChatSelectedNow = chatData;

                }, reject);

        });
    }

    /**
     * Create new chat
     *
     * @param contactId
     * @returns {Promise<any>}
     */
    createNewChat(contactId:any): Promise<any>
    {
        return new Promise((resolve, reject) => {

            const contact = this.contacts.find((item) => {
                return item.id === contactId;
            });

            const chatId = FuseUtils.generateGUID();

            const chat = {
                id    : chatId,
                userId: this.user.id,
                contactId : contactId,
                dialog: []
            };
            console.log(contact);
            const chatListItem = {
                contactId      : contactId,
                id             : chatId,
                lastMessageTime: new Date(),
                name           : contact.name,
                unread         : null
            };

            // Add new chat list item to the user's chat list
            this.user.chatList.push(chatListItem);

            // Post the created chat
            this._httpClient.post( this.utilitesService.urlPrefix + "chat/mensagens", {...chat }, {headers: this.authService.getAuthHeaders()})
                .subscribe((response: any) => {

                    // Post the new the user data
                    this._httpClient.put(this.utilitesService.urlPrefix + "chat/usuario/new-chat/" + this.user.id, this.user, { headers: this.authService.getAuthHeaders() })
                        .subscribe(newUserData => {

                            // Update the user data from server
                            this.getUser().then(updatedUser => {
                                this.onUserUpdated.next(updatedUser);
                                resolve(updatedUser);
                            });
                        });

                        
                }, reject);
        });
    }

    /**
     * Select contact
     *
     * @param contact
     */
    selectContact(contact:any): void
    {
        this.onContactSelected.next(contact);
    }

    /**
     * Set user status
     *
     * @param status
     */
    setUserStatus(status:any): void
    {
        this.user.status = status;
    }

    /**
     * Update user data
     *
     * @param userData
     */
    updateUserData(userData:any): void
    {
        this._httpClient.put(this.utilitesService.urlPrefix + "chat/usuario/" + this.user.id, userData, { headers: this.authService.getAuthHeaders() })
            .subscribe((response: any) => {
                    this.user = userData;
                }
            );
    }


    /**
     * Update the chat dialog
     *
     * @param chatId
     * @param dialog
     * @returns {Promise<any>}
     */
    updateDialog(chatId:any, dialog:any): Promise<any>
    {
        return new Promise((resolve, reject) => {

            const newData = {
                id    : chatId,
                dialog: dialog
            };

            this._httpClient.put( this.utilitesService.urlPrefix + "chat/mensagens/" + chatId, newData, { headers: this.authService.getAuthHeaders() })
                .subscribe(updatedChat => {
                    resolve(updatedChat);
                }, reject);

            //this.sendMessage(newData);
        });
    }

    /**
     * Get contacts
     *
     * @returns {Promise<any>}
     */
    getContacts(): Promise<any>
    {
        return new Promise((resolve, reject) => {
            this._httpClient.get(this.utilitesService.urlPrefix + "chat/usuario/getUsersContact", { headers: this.authService.getAuthHeaders() })
                .subscribe((response: any) => {                    
                    resolve(response.data);
                }, reject);
        });
    }

    /**
     * Get chats
     *
     * @returns {Promise<any>}
     */
    getChats(): Promise<any>
    {
        return new Promise((resolve, reject) => {
            this._httpClient.get(this.utilitesService.urlPrefix + "chat/mensagens", { headers: this.authService.getAuthHeaders() })
                .subscribe((response: any) => {
                    resolve(response.data);
                }, reject);
        });
    }

    /**
     * Get user
     *
     * @returns {Promise<any>}
     */
    getUser(): Promise<any>
    {
        return new Promise((resolve, reject) => {
            this._httpClient.get(this.utilitesService.urlPrefix + "chat/usuario/getUsuario", { headers: this.authService.getAuthHeaders() })
                .subscribe((response: any) => {                    
                    resolve(response.data);
                }, reject);
        });
    }

    /**
     * Atualiza indicador do CHAT
     * @param params Json Conversa Chat
     */
    atualizaNotificacao(id: number, params: any):Observable<any>{
        return this._httpClient.put(this.utilitesService.urlPrefix + "chat/mensagens/notificacao/"+id, JSON.stringify(params), { headers: this.authService.getAuthHeaders() });
    }

    atualizaUsuario(){
       // Update the user data from server
       return new Promise((resolve, reject) => {
            this.getUser().then(updatedUser => {
                this.onUserUpdated.next(updatedUser);
                console.log(updatedUser);
                resolve(updatedUser);
            });
        });
    }
}
